import {inject, Injectable} from '../di';
import leadingZeros from '../utils/leading-zeros';
import {Logger} from './logger';
import {AppInitializeService} from '../app-initialize-service';
import {StorageService} from '../storage-service';
import {TimeService} from '../time-service';

@Injectable()
export class LegacyPersistentLogService implements AppInitializeService {
  private readonly logger = inject(Logger);
  private readonly storageService = inject(StorageService);
  private readonly timeService = inject(TimeService);

  private log: Array<string | LegacyPersistentLogEntry> = [];

  //max 20 categories, max 50 events in category
  private readonly CODES: {[category in LegacyPersistentLogCategory]: string[]} = {
    RELOAD: [
      'service code',
      'test page',
      'F5',
      'forced!',
      'mapping autoconfigure',
      'checkin',
      'checkout',
      'content update',
      'event restart',
      'sys command',
      'rc command',
      'service code',
      'test page',
      'change language',
      'load timeout',
      'load error',
      'open not idle app',
      'long standby',
      'UPDOWN service code',
      'power off',
      'power on (hack)',
      'room set by pin',
      'room set',
      'auto power off',
    ],
    REBOOT: [
      'checkin',
      'checkout',
      'test page',
      'rs232',
      'long standby (hack)',
      'auto power off',
      'LG fullHD auto',
      'LG 720 set',
      'LG 1080 set',
      'LG FullHD toggle',
      'LG SDAP',
      'Vestel FullHD toggle',
    ],
    POWERON: [
      'TVIP load (hack)',
      'event wol',
      'event wol (get power fail)',
      'rs232 p01 cmd',
      'rs232 p02 cmd',
      'rs232 p06 cmd',
      'rs232 t cmd',
      'rs232 D cmd',
      'rc power',
      'TVIP power btn (hack)',
      'RC channel',
    ],
    POWEROFF: [
      'checkin',
      'checkout',
      'event poweroff',
      'rs232 C cmd',
      'rs232 E cmd',
      'rc power',
      'test page',
      '12h idle on',
      'sleep timer',
      'auto power off',
      'TVIP power btn (hack)',
      'cec sleep',
      'cec wake',
    ],
    SYSTEM: ['loaded', 'standby', 'proceed'],
  };

  public async init(): Promise<boolean> {
    const preservedLogs = this.storageService.getJson<Array<string | LegacyPersistentLogEntry>>('persistent_logs');
    if (preservedLogs && preservedLogs.length) {
      this.log = preservedLogs.concat(this.log);
    }

    // TODO: check persistent.CODES constant

    if (this.log.length > 300) {
      // 300 entries is about 3kb in localStorage
      this.log = this.log.slice(-300);
    }

    return Promise.resolve(true);
  }

  /**
   * Add persistent log entry
   * @param {string} txt log message. May be preceded by a category, a colon, and a space (e.g. `RELOAD: Message text`).
   * @param {LegacyPersistentLogCategory} [cat] log category
   */
  public add(txt: string, cat?: LegacyPersistentLogCategory) {
    if (!cat) {
      try {
        const match = txt.match(/(.+?): (.+)$/);
        if (match) {
          txt = match[2];
          cat = match[1] as LegacyPersistentLogCategory;
        }
      } catch (err) {
        this.logger.error(`[LegacyPersistentLogService] Unable to parse persistent log message: ${txt}`, err);
      }
    }

    const logEntry: LegacyPersistentLogEntry = {
      time: Date.now(),
      cat: cat || undefined,
      text: txt,
    };

    const logEntryPacked = this.pack(logEntry);

    this.log.push(logEntryPacked);

    this.storageService.set('persistent_logs', this.log);
  }

  public out() {
    const out = this.log
      .map((entry) => {
        let color = '';
        if (typeof entry === 'string') {
          entry = this.unpack(entry);
          color = 'color: Green;';
        }
        const date = this.formatLogEntryDate(entry);
        const cat = entry.cat ? `${entry.cat}: ` : '';

        return `<span style="font-family: monospace;${color}">${date}:</span> ${cat}${entry.text}`;
      })
      .join('<br/>');

    let logPersistentEl = $('#log_persistent');
    if (!logPersistentEl.length) {
      logPersistentEl = $('#sample_page').clone(true);
      logPersistentEl.attr('id', 'log_persistent');
      logPersistentEl.attr('scroll_to_bottom', 'true');
      logPersistentEl.find('.content').css({width: '100%', margin: '0px'});
      logPersistentEl.find('.header').html('<h1>Persistent Log</h1>');
      $('#container').append(logPersistentEl);
    }

    if (document.getElementById('tests_logs')) {
      logPersistentEl
        .find('.header')
        .html('<div class="back" href_type="back" href=\'#tests_logs\'></div><h1>Persistent Log</h1>');
    }
    logPersistentEl.find('.content').html(`<div>${out}</div>`);

    // TODO legacy navigation
    window.navigate('#log_persistent', 'now');
  }

  /**
   * Output all persistent logs to the console.
   */
  public console() {
    this.log.forEach((entry) => {
      if (typeof entry === 'string') {
        entry = this.unpack(entry);
      }
      const date = this.formatLogEntryDate(entry);
      const cat = entry.cat ? `${entry.cat}: ` : '';
      console.log(`${date}: ${cat}${entry.text}`);
    });
  }

  /**
   * Clear persistent logs
   */
  public clear() {
    this.log = [];
    this.storageService.remove('persistsent_logs');
  }

  private pack(entry: LegacyPersistentLogEntry): string | LegacyPersistentLogEntry {
    if (entry.cat) {
      const categoryEvents = this.CODES[entry.cat];
      const entryCategoryIndex = Object.keys(this.CODES).indexOf(entry.cat);
      const entryEventIndex = categoryEvents.indexOf(entry.text);
      if (entryCategoryIndex !== -1 && entryEventIndex !== -1) {
        let out: string;
        out = (Math.floor(entry.time / 1000) - 1720000000).toString();
        out = out + leadingZeros(entryCategoryIndex * 20 + entryEventIndex, 3);
        out = parseInt(out).toString(36);
        return out;
      }
    }
    return entry;
  }

  private unpack(packed: string): LegacyPersistentLogEntry {
    const encoded = parseInt(packed, 36);
    const code = encoded % 1000;
    const cat = Object.keys(this.CODES)[Math.floor(code / 20)] as LegacyPersistentLogCategory;
    const time = 1720000000000 + encoded;
    const text = this.CODES[cat][code % 20];
    return {
      time: time,
      cat: cat,
      text: text,
    };
  }

  private formatLogEntryDate(entry: LegacyPersistentLogEntry): string {
    const date = this.timeService.getRealDate(entry.time);
    return date.format('DD.MM HH:mm:ss');
  }
}

export enum LegacyPersistentLogCategory {
  Reload = 'RELOAD',
  Reboot = 'REBOOT',
  PowerOn = 'POWERON',
  PowerOff = 'POWEROFF',
  System = 'SYSTEM',
}

interface LegacyPersistentLogEntry {
  /**
   * Log message
   */
  text: string;

  /**
   * Log Entry Category
   */
  cat?: LegacyPersistentLogCategory;

  /**
   * Date of log entry creation.
   * This is a local date, without any adjustment for time zone or server time.
   */
  time: number;
}
